import { lazy } from 'react';
import { trim } from 'lodash';
import { isAnyOf } from '@reduxjs/toolkit';
import {
  signInWithEmailAndPassword, EmailAuthProvider, linkWithCredential, createUserWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { batch } from 'react-redux';
import { trackAnalytics } from '@client/intercom';
import { showError } from '@client/listeners/lib/alerts';
import { firebaseRequest, handleRequest } from '@client/listeners/lib/requests';
import { showLoginErrorSnackbar, showSnackbar } from '@client/listeners/lib/snackbar';
import { setLoaderIsLoading, setLoaderIsFinishedSuccess, setLoaderIsFinishedError } from '@client/slices/helpers/loaders.reducer';
import {
  setCurrentSessionUser, startEmailLogin, startEmailSignup, startSendForgotPasswordEmail,
} from '@client/slices/helpers/session.reducer';
import { createUser } from '@client/listeners/utils/session';
import { Alert } from '@client/components/globals/Modal';
import { getDateTime } from '@sda/utils/date';
import { Loader } from '@client/components/common/Loader';
import type { AppDispatch, RootState } from '@client/store';
import type { ListenerEffectAPI } from '@reduxjs/toolkit';
import type { EmailSignupPayload, SigninPayload } from '@sda/interfaces/reducer/session.reducer';
import type { AddListener } from '@client/listeners/lib';

const EmailLoginModalLoader = lazy(() => import('./EmailLoginModal'));

interface SigninLinkProps {
  readonly email: string;
  readonly scope: string;
  readonly userId?: string;
}

export const sendLoginLink = async (listenerApi: ListenerEffectAPI<RootState, AppDispatch>, props: SigninLinkProps) => {
  const { email, scope, userId } = props;
  const { dispatch } = listenerApi;

  const linkRequest = await firebaseRequest({
    url: `${process.env.API_BASE_URL}/system/signInLink`,
    method: 'POST',
    body: {
      email,
      timestamp: getDateTime().toLocaleString(),
      userId: userId || null,
    },
  });

  const linkResponse = await handleRequest(linkRequest);

  if (linkResponse.status) {
    if (window.localStorage?.setItem) {
      window.localStorage.setItem('oobEmail', email);
    }

    dispatch(setLoaderIsFinishedSuccess(scope));
    showSnackbar('A link to finish logging in has been emailed to you.');
  } else {
    dispatch(setLoaderIsFinishedError(scope, linkResponse.error));
  }
};

export const signedOutListeners = (addListener: AddListener) => [
  addListener({
    actionCreator: startSendForgotPasswordEmail,
    effect: async (action, listenerApi) => {
      const { email, id } = action.payload;

      const { dispatch } = listenerApi;

      const scope = 'forgot-password-button';
      dispatch(setLoaderIsLoading(scope));

      try {
        const request = await firebaseRequest({
          url: `${process.env.API_BASE_URL}/system/forgotPassword`,
          method: 'POST',
          body: {
            id,
            email: trim(email),
          },
        });

        const response = await handleRequest(request);

        if (response.status) {
          dispatch(setLoaderIsFinishedSuccess(scope));
          showSnackbar('A password reset email has been sent to you');
        } else {
          dispatch(setLoaderIsFinishedError(scope, response.error));
        }

        trackAnalytics(startSendForgotPasswordEmail);
      } catch (e) {
        if (e.message === 'Not Found') {
          dispatch(setLoaderIsFinishedSuccess(scope));
        } else {
          showError(e);
          dispatch(setLoaderIsFinishedError(scope, e));
        }
      }
    },
  }),

  addListener({
    matcher: isAnyOf(startEmailLogin, startEmailSignup),
    effect: async (action, listenerApi) => {
      const { intent = null, ...credentials } = action.payload as EmailSignupPayload & SigninPayload;

      const { dispatch } = listenerApi;
      const firebase = window.Firebase;

      const scope = 'email-password-form';
      dispatch(setLoaderIsLoading(scope));

      const MFA_REQUIRED = 'MFA_REQUIRED';

      const { email, password, acceptsTos = false } = credentials;
      const trimmedEmail = trim(email);

      try {
        const { currentUser } = firebase.auth;
        const isAnonymous = currentUser && currentUser.isAnonymous;

        if (startEmailLogin.match(action)) {
          if (currentUser) {
            await firebase.auth.signOut();
          }

          const validationRequest = await firebaseRequest({
            url: `${process.env.API_BASE_URL}/system/validateLogin`,
            method: 'POST',
            body: {
              email: trimmedEmail,
            },
          });

          const validationResponse = await handleRequest<boolean>(validationRequest);

          if (validationResponse.status === true) {
            await signInWithEmailAndPassword(firebase.auth, trimmedEmail, password);

            if (validationResponse.data) {
              await signOut(firebase.auth);

              await sendLoginLink(listenerApi, { email: trimmedEmail, scope });

              throw new Error(MFA_REQUIRED);
            }
          } else {
            throw new Error('Credentials not found');
          }

          trackAnalytics(startEmailLogin, { email });
        } else if (currentUser && isAnonymous) {
          const emailCredential = EmailAuthProvider.credential(trimmedEmail, password);
          const { user: newUser } = await linkWithCredential(firebase.auth.currentUser, emailCredential);

          await createUser({
            ...newUser, ...credentials, acceptsTos, intent,
          }, listenerApi);

          trackAnalytics(startEmailSignup, { email });
        } else {
          const { user: newUser } = await createUserWithEmailAndPassword(firebase.auth, trimmedEmail, password);

          await createUser({
            ...newUser, ...credentials, acceptsTos, intent,
          }, listenerApi);

          trackAnalytics(startEmailSignup, { email });
        }

        dispatch(setLoaderIsFinishedSuccess('email-password-form'));
      } catch (e) {
        if (e?.message === MFA_REQUIRED) {
          Alert.show({
            title: ' ',
            buttons: [],
            body: (
              <Loader
                load={EmailLoginModalLoader}
                email={trimmedEmail}
                callback={() => sendLoginLink(listenerApi, { email: trimmedEmail, scope })}
                scope={scope}
              />
            ),
          });
        } else {
          showLoginErrorSnackbar(e);
        }

        batch(() => {
          dispatch(setCurrentSessionUser(false));
          dispatch(setLoaderIsFinishedError('email-password-form', e));
        });
      }
    },
  }),
];
